// SaaS-Elite Toast Utility with Spring Animations & Glassmorphism
const TOAST_DURATION = 6000;

interface ToastOptions {
  title: string;
  message?: string;
  type: 'success' | 'error' | 'info';
  icon: string;
  color: string;
}

const createToast = ({ title, message, type, icon }: ToastOptions) => {
  if (typeof window === 'undefined') return;

  // 1. Get or create container with improved spacing
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'fixed top-6 right-6 z-[99999] flex flex-col gap-3 pointer-events-none w-[calc(100%-3rem)] sm:max-w-sm';
    document.body.appendChild(container);
  }

  // 2. Create toast element with advanced styling
  const toastEl = document.createElement('div');
  toastEl.className = `
    group pointer-events-auto relative overflow-hidden
    bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl 
    border border-slate-200/60 dark:border-white/10 
    rounded-2xl p-3.5 pl-4 flex gap-3.5 items-center
    transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]
    scale-90 translate-x-8 opacity-0
    hover:scale-[1.01] hover:shadow-2xl
  `;
  
  // High-fidelity layered shadows
  toastEl.style.boxShadow = '0 10px 30px -5px rgba(0, 0, 0, 0.1), 0 4px 12px -4px rgba(0, 0, 0, 0.05)';

  const colors = {
    success: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', bar: 'bg-emerald-500' },
    error: { bg: 'bg-rose-500/10', text: 'text-rose-500', bar: 'bg-rose-500' },
    info: { bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400', bar: 'bg-indigo-500' }
  }[type];

  toastEl.innerHTML = `
    <div class="flex-shrink-0 w-9 h-9 rounded-xl ${colors.bg} flex items-center justify-center ${colors.text}">
      <span class="material-symbols-outlined text-[22px]" style="font-variation-settings: 'FILL' 1; font-weight: 300;">${icon}</span>
    </div>
    <div class="flex-1 min-w-0">
      <div class="text-[14px] font-black text-slate-900 dark:text-white leading-tight tracking-tight">${title}</div>
      ${message ? `<div class="text-[12px] font-bold text-slate-500 dark:text-slate-400 mt-0.5 leading-snug line-clamp-2">${message}</div>` : ''}
    </div>
    <button class="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-all duration-200">
      <span class="material-symbols-outlined text-[18px]">close</span>
    </button>
    <div class="absolute bottom-0 left-0 h-[2px] ${colors.bar} opacity-40 transition-all duration-[6000ms] ease-linear w-full origin-left" id="progress-${Date.now()}"></div>
  `;

  container.appendChild(toastEl);

  // 3. Trigger bouncy entry animation
  requestAnimationFrame(() => {
    toastEl.classList.remove('scale-90', 'translate-x-8', 'opacity-0');
    toastEl.classList.add('scale-100', 'translate-x-0', 'opacity-100');
  });

  // 4. Progress bar animation
  const progress = toastEl.querySelector('div[id^="progress-"]') as HTMLElement;
  if (progress) {
    requestAnimationFrame(() => {
      progress.style.width = '0%';
    });
  }

  // 5. Dismiss logic with smooth exit
  const dismiss = () => {
    toastEl.classList.add('scale-95', 'opacity-0', 'translate-y-1');
    setTimeout(() => {
      if (container?.contains(toastEl)) {
        container.removeChild(toastEl);
        if (container.childElementCount === 0) {
          document.body.removeChild(container);
        }
      }
    }, 400);
  };

  const timer = setTimeout(dismiss, TOAST_DURATION);

  toastEl.querySelector('button')?.addEventListener('click', (e) => {
    e.stopPropagation();
    clearTimeout(timer);
    dismiss();
  });
};

export const toast = {
  success: (title: string, message?: string) => {
    createToast({ title, message, type: 'success', icon: 'check_circle', color: 'emerald' });
  },
  error: (title: string, message?: string) => {
    createToast({ title, message, type: 'error', icon: 'warning', color: 'rose' });
  },
  info: (title: string, message?: string) => {
    createToast({ title, message, type: 'info', icon: 'info', color: 'indigo' });
  }
};